import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { EmptyState, PageHeader } from "../components/primitives";
import { IconContent } from "../components/icons";
import { apiFetch, type IssueRow } from "../lib/api";
import { ApiError, copyTemplate, fetchTemplates, setTemplate, type Template } from "../lib/api-automations";
import { renderPreview, type PreviewSection } from "../lib/newsletter-html";
import { relativeTime } from "../lib/dates";
import { useTopicLabel } from "./BroadcastsPage";

/**
 * Design Studio: the newsletter designs the team reuses.
 *
 * A template is a newsletter like any other — designed in the same editor —
 * marked for reuse. Its open article slots are what an automation fills each
 * week. Start a new newsletter from one, or point an automation at it.
 */
export function DesignStudioPage() {
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [adding, setAdding] = useState(false);

  const load = () => {
    fetchTemplates().then(({ data }) => setTemplates(data)).catch((e: ApiError) => setError(e));
  };
  useEffect(load, []);

  return (
    <>
      <PageHeader
        title="Design Studio"
        meta="Newsletter designs your team reuses. Automations build from these every week."
        actions={adding ? undefined : (
          <button type="button" className="nl-btn-primary" onClick={() => setAdding(true)}>Add a template</button>
        )}
      />
      {adding && <AddTemplate onDone={() => { setAdding(false); load(); }} />}

      {error ? (
        <EmptyState
          title={error.pendingMigration ? "Design Studio is waiting for a database update" : "Templates could not be loaded"}
          body={error.message}
          hint={error.pendingMigration ? `Run ${error.pendingMigration} in Supabase, then reload.` : "Reload the page to try again."}
        />
      ) : templates === null ? (
        <p className="bc-loading">Loading templates…</p>
      ) : templates.length === 0 ? (
        <EmptyState
          icon={<IconContent width={22} height={22} />}
          title="No templates yet"
          body="Design a newsletter in Newsletters, then add it here to reuse it and to build automations from it."
          hint="A template's empty article blocks are filled with the newest articles each time it's used."
        />
      ) : (
        <ul className="ds-grid">
          {templates.map((t) => <TemplateCard key={t.id} t={t} onChange={load} />)}
        </ul>
      )}

      <BrandStyles />
    </>
  );
}

function TemplateCard({ t, onChange }: { t: Template; onChange: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const topic = useTopicLabel();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The template as it renders, empty slots marked, shrunk to a thumbnail.
  const html = useMemo(() => renderPreview({
    title: t.title, subject: t.subject, preheader: t.preheader ?? undefined, articles: [],
    sections: (t.sections ?? []) as PreviewSection[],
  }, { editable: true }).html, [t]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await copyTemplate(t.id);
      // The editor opens newsletters from its list; fetch it again so the
      // copy is in it, rather than arriving at "not found".
      await queryClient.invalidateQueries({ queryKey: ["issues"] });
      navigate(`/content/${data.id}`);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    await setTemplate(t.id, false).then(onChange).catch((e: Error) => { setError(e.message); setBusy(false); });
  };

  return (
    <li className="ds-card">
      <div className="ds-thumb" aria-hidden>
        <iframe title="" srcDoc={html} sandbox="" tabIndex={-1} />
      </div>
      <div className="ds-card-body">
        <h2 className="ds-card-title">{t.title}</h2>
        <p className="ds-card-meta">
          {t.categories.length ? <span lang="ar" dir="rtl">{t.categories.map(topic).join("، ")}</span> : "No topics yet"}
          {" · "}Edited {relativeTime(t.updated_at)}
        </p>
        <div className="ds-card-actions">
          <button type="button" className="nl-btn-secondary" onClick={start} disabled={busy}>New newsletter from this</button>
          <Link className="nl-text-btn" to={`/automations?template=${t.id}`}>Automate weekly</Link>
          <Link className="nl-text-btn" to={`/content/${t.id}`}>Edit design</Link>
          <button type="button" className="nl-text-btn ds-remove" onClick={remove} disabled={busy}>Remove</button>
        </div>
        {error && <p className="bc-error" role="alert">{error}</p>}
      </div>
    </li>
  );
}

/** Pick any saved newsletter and add it to the library. */
function AddTemplate({ onDone }: { onDone: () => void }) {
  const [issues, setIssues] = useState<IssueRow[] | null>(null);
  const [id, setId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch("/api/v1/issues")
      .then((r) => r.json())
      .then((b: { data?: IssueRow[] }) => setIssues(b.data ?? []))
      .catch(() => setIssues([]));
  }, []);

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await setTemplate(id, true);
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <section className="bc-new" aria-label="Add a template">
      <label className="bc-field">
        <span>Newsletter to reuse as a template</span>
        <select className="nl-builder-select" value={id} onChange={(e) => setId(e.target.value)}>
          <option value="">{issues === null ? "Loading…" : issues.length ? "Choose a newsletter" : "No saved newsletters yet"}</option>
          {(issues ?? []).map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
        </select>
      </label>
      {error && <p className="bc-error" role="alert">{error}</p>}
      <div className="bc-new-actions">
        <button type="button" className="nl-btn-secondary" onClick={onDone} disabled={busy}>Cancel</button>
        <button type="button" className="nl-btn-primary" onClick={add} disabled={busy || !id}>Add to templates</button>
      </div>
    </section>
  );
}

/**
 * The brand every template inherits. Read from the app's tokens, which come
 * from brand.altibbi.app; changed there, never here.
 */
function BrandStyles() {
  const colours = [
    { token: "--brand-500", name: "Brand", use: "Buttons and links" },
    { token: "--ink", name: "Ink", use: "Headings and body" },
    { token: "--ink-3", name: "Ink 3", use: "Captions" },
    { token: "--surface-2", name: "Surface 2", use: "Email background" },
    { token: "--line", name: "Line", use: "Dividers" },
  ];
  return (
    <section className="ds-brand" aria-label="Brand styles">
      <h2 className="nl-panel-title">Brand styles</h2>
      <p className="nl-panel-sub">
        Every template uses these. They come from <a href="https://brand.altibbi.app" target="_blank" rel="noopener noreferrer">brand.altibbi.app</a> and change there.
      </p>
      <div className="ds-brand-row">
        <ul className="ds-swatches">
          {colours.map((c) => (
            <li key={c.token}>
              <span className="ds-swatch" style={{ background: `var(${c.token})` }} aria-hidden />
              <span><strong>{c.name}</strong><em>{c.use}</em></span>
            </li>
          ))}
        </ul>
        <dl className="ds-type">
          <div><dt>Headings</dt><dd style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>Tajawal · الطبي</dd></div>
          <div><dt>Body</dt><dd style={{ fontFamily: "var(--font-text)" }}>IBM Plex Sans Arabic · نشرة الطبي</dd></div>
        </dl>
      </div>
    </section>
  );
}
